/**
 * Break timer for timeouts and the breaks between periods (§5.2). Wall-clock based like the
 * game clock: nothing counts down, the display value is computed from `runningSince`.
 * It does not stop by itself at 0:00; the UI sounds the buzzer and the operator starts play.
 */
import type { BreakKind } from '../rules/ruleset';

export interface BreakTimer {
  kind: BreakKind;
  /** Wall ms when the timer last started; null when paused. */
  runningSince: number | null;
  /** Remaining ms at `runningSince`, or the current value when paused. */
  remainingMsAtStart: number;
}

export function startBreak(kind: BreakKind, remainingMs: number, now: number): BreakTimer {
  return { kind, runningSince: now, remainingMsAtStart: Math.max(0, remainingMs) };
}

/** Break time left in ms at `now` (never below 0). */
export function breakRemaining(timer: BreakTimer, now: number): number {
  if (timer.runningSince === null) return timer.remainingMsAtStart;
  return Math.max(0, timer.remainingMsAtStart - Math.max(0, now - timer.runningSince));
}

export function isBreakRunning(timer: BreakTimer): boolean {
  return timer.runningSince !== null;
}

/** Pause at `now`. `authoritativeMs` (from the stored event) overrides the computed value. */
export function pauseBreak(timer: BreakTimer, now: number, authoritativeMs?: number): BreakTimer {
  return {
    ...timer,
    runningSince: null,
    remainingMsAtStart: Math.max(0, authoritativeMs ?? breakRemaining(timer, now)),
  };
}

export function resumeBreak(timer: BreakTimer, now: number, remainingMs?: number): BreakTimer {
  return {
    ...timer,
    runningSince: now,
    remainingMsAtStart: Math.max(0, remainingMs ?? breakRemaining(timer, now)),
  };
}

/** Set the timer to `remainingMs`, keeping it running or paused. */
export function adjustBreak(timer: BreakTimer, remainingMs: number, now: number): BreakTimer {
  return {
    ...timer,
    runningSince: timer.runningSince === null ? null : now,
    remainingMsAtStart: Math.max(0, remainingMs),
  };
}
